import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { useNotificationContext } from "cerulean";
import { Button, Dialog, IconButton } from "@narmi/design_system";

import {
  createBankingToken,
  deserializeBankingToken,
  retrieveBankingToken,
  updateBankingToken,
} from "./api";
import { DisplayOnceTokenDialog } from "./modals";
import {
  TokenDialogSubmitFooter,
  TokenRevokeDialog,
  useTokenForm,
} from "./tokenWriteFormModals";
import { CreatedBankingTokenRecord, DeserializedBankingToken } from "./types";
import SudoWritePane from "./SudoWritePane";
import useSudoWrite, { isSudoCancelled } from "./useSudoWrite";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import styles from "./tokenForm.module.scss";

const LIST_ROUTE = "/api_banking_token_list";

type TokenFormMode = "create" | "edit" | "view";

/**
 * Renders the standalone create/edit/view token page body. Split out from the
 * page shell so `useTokenForm`'s state initializers receive the fetched token
 * in edit/view mode.
 */
const TokenFormContent = ({
  mode,
  updateToken,
  returnPath,
}: {
  mode: TokenFormMode;
  updateToken?: DeserializedBankingToken;
  returnPath: string;
}) => {
  const { l10n } = useLocalization();
  const { currentUser } = useCurrentUser();
  const navigate = useNavigate();
  const { sendNotification } = useNotificationContext();
  const isEdit = mode === "edit";
  const isView = mode === "view";
  // The view pane presents every field read-only; write affordances (submit
  // footer, "use my IP", editable inputs) are suppressed downstream.
  const readOnly = isView;
  const [createdToken, setCreatedToken] =
    useState<CreatedBankingTokenRecord | null>(null);
  const [tokenForRevoke, setTokenForRevoke] =
    useState<DeserializedBankingToken | null>(null);
  // In edit mode, saving is confirmed through a modal since scope/IP removals
  // take effect immediately against a live token.
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  // Wraps writes so a sudo-required response opens the MFA modal, then retries.
  const runWrite = useSudoWrite(returnPath);

  const backToList = () => navigate(LIST_ROUTE);

  // Mirrors the token list: write access maps to the `change_bankingaccesstoken`
  // permission — limited to org admins and the token's creator, and only while
  // the token is still active. Read access (`view_bankingaccesstoken`) is
  // implied for any token the user can load here.
  const canWriteToken = (token: DeserializedBankingToken) =>
    Boolean(
      currentUser &&
        (currentUser.org_role === "Admin" ||
          token.createdBy === currentUser.username) &&
        !token.isExpired,
    );

  const canChangeUpdateToken = Boolean(
    updateToken && canWriteToken(updateToken),
  );

  // A user who can only view a token (lacks `change`) has no editable surface,
  // so an edit deep-link falls back to the read-only view pane. `currentUser` is
  // required in the guard so we don't redirect before it has loaded (and so the
  // pane behaves predictably in tests where no user is provided).
  useEffect(() => {
    if (isEdit && currentUser && updateToken && !canChangeUpdateToken) {
      navigate(`${LIST_ROUTE}/${updateToken.uuid}/view`, { replace: true });
    }
  }, [isEdit, currentUser, updateToken, canChangeUpdateToken, navigate]);

  const { tokenForm, handleConfirm } = useTokenForm({
    updateToken,
    readOnly,
    onSubmit: async (data) => {
      try {
        if (isEdit && updateToken) {
          await runWrite(() => updateBankingToken(updateToken.uuid, data));
          sendNotification({
            type: "success",
            text: l10n.getString("notification-token-updated"),
          });
          backToList();
          return;
        }
        const created = await runWrite(() => createBankingToken(data));
        sendNotification({
          type: "success",
          text: l10n.getString("notification-token-created"),
        });
        setCreatedToken(created);
      } catch (error) {
        // Swallow user-cancelled MFA; surface everything else in the form alert.
        if (isSudoCancelled(error)) return;
        throw error;
      }
    },
  });

  return (
    <div className="padding--top--l">
      <DisplayOnceTokenDialog
        isOpen={Boolean(createdToken)}
        onUserDismiss={() => {
          setCreatedToken(null);
          backToList();
        }}
        createdToken={createdToken}
      />

      <TokenRevokeDialog
        isOpen={Boolean(tokenForRevoke)}
        onUserDismiss={() => setTokenForRevoke(null)}
        onConfirm={() => {
          setTokenForRevoke(null);
          sendNotification({
            type: "success",
            text: l10n.getString("notification-token-revoked"),
          });
          backToList();
        }}
        tokenForRevoke={tokenForRevoke}
        runWrite={runWrite}
      />

      <div>
        <div className={styles.pageHeader}>
          <div className={styles.pageHeaderMain}>
            <IconButton
              name="chevron-left"
              label={l10n.getString("aria-label-back")}
              onClick={backToList}
            />
            <h1 className="fontFamily--heading fontSize--heading1 fontWeight--bold margin--none">
              {mode === "create"
                ? l10n.getString("dialog-create-token-title")
                : updateToken?.nickname ||
                  l10n.getString("dialog-edit-token-title")}
            </h1>
          </div>
          {isView && canChangeUpdateToken && updateToken && (
            <div className={styles.pageHeaderActions}>
              <Button
                kind="tertiary"
                startIcon="edit-2"
                onClick={() =>
                  navigate(`${LIST_ROUTE}/${updateToken.uuid}/edit`)
                }
                label={l10n.getString("menu-item-edit")}
              />
              <Button
                kind="tertiary"
                startIcon="trash"
                onClick={() => setTokenForRevoke(updateToken)}
                label={l10n.getString("menu-item-delete")}
              />
            </div>
          )}
          {isEdit && canChangeUpdateToken && updateToken && (
            <div className={styles.pageHeaderActions}>
              <Button
                kind="tertiary"
                startIcon="trash"
                onClick={() => setTokenForRevoke(updateToken)}
                label={l10n.getString("menu-item-delete")}
              />
            </div>
          )}
        </div>
        <div className="padding--top--m fontColor--secondary">
          {mode === "create"
            ? l10n.getString("text-create-token-description")
            : l10n.getString("text-edit-token-description")}
        </div>
      </div>

      {tokenForm}

      {!readOnly && (
        <div className="padding--top--l">
          <TokenDialogSubmitFooter
            buttonText={
              isEdit
                ? l10n.getString("button-update-token")
                : l10n.getString("button-create-token")
            }
            onCancel={backToList}
            onConfirm={() =>
              isEdit ? setShowUpdateConfirm(true) : handleConfirm()
            }
          />
        </div>
      )}

      {isEdit && (
        <Dialog
          title={l10n.getString("dialog-update-token-title")}
          isOpen={showUpdateConfirm}
          onUserDismiss={() => setShowUpdateConfirm(false)}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "20px" }}>
              <Button kind="plain" onClick={() => setShowUpdateConfirm(false)}>
                {l10n.getString("button-cancel")}
              </Button>
              <Button
                onClick={() => {
                  setShowUpdateConfirm(false);
                  handleConfirm();
                }}
              >
                {l10n.getString("button-update-token")}
              </Button>
            </div>
          }
        >
          <p>{l10n.getString("dialog-update-token-message")}</p>
        </Dialog>
      )}
    </div>
  );
};

const BusinessBankingAPITokenForm = ({
  mode,
}: {
  mode?: TokenFormMode;
}) => {
  const { tokenId } = useParams();
  // Fall back to deriving the mode from the presence of a token id so callers
  // (and existing tests) that don't pass `mode` still resolve create vs edit.
  const resolvedMode: TokenFormMode = mode ?? (tokenId ? "edit" : "create");
  const [token, setToken] = useState<DeserializedBankingToken | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(tokenId));

  // Base path of this pane; the scoped MFA modal routes its OTP screens under
  // it so the form stays mounted (preserving the one-time token dialog).
  const basePath = tokenId
    ? `api_banking_token_list/${tokenId}/${resolvedMode === "view" ? "view" : "edit"}`
    : "api_banking_token_list/create";

  useEffect(() => {
    if (!tokenId) return;
    retrieveBankingToken(tokenId)
      .then((record) => setToken(deserializeBankingToken(record)))
      .finally(() => setIsLoading(false));
  }, [tokenId]);

  if (isLoading) {
    return null;
  }

  return (
    <SudoWritePane basename={basePath}>
      <TokenFormContent
        mode={resolvedMode}
        updateToken={token ?? undefined}
        returnPath={`/${basePath}`}
      />
    </SudoWritePane>
  );
};

export default BusinessBankingAPITokenForm;
